import { memo, useState } from 'react'
import { Link } from 'react-router-dom'
import type { ContratoParcela, ParcelaMedicao } from '@services/db.js'
import { Badge } from '@components/primitives/Badge.js'
import { formatCurrency, formatDate } from '@utils/format.js'
import { FORMAS_PAGAMENTO, ROUTES } from '@utils/constants.js'
import { CONDICAO_LABEL, PARCELA_STATUS_LABEL, PARCELA_STATUS_VARIANT } from './contratosLabels.js'

const FORMA_LABEL: Record<string, string> = Object.fromEntries(
  FORMAS_PAGAMENTO.map((f) => [f.value, f.label]),
)

interface Props {
  parcela: ContratoParcela
  formaPagamento?: string
  contratoAtivo: boolean
  /** Abre a vistoria (checklist + fotos) antes de liberar. */
  onMedir: (parcela: ContratoParcela) => void
  onDesfazerLiberar: (id: number) => Promise<void>
  onPagar: (parcela: ContratoParcela) => void
  onDesfazerPagar: (id: number) => Promise<void>
  onEditar: (parcela: ContratoParcela) => void
  onRemover: (id: number) => Promise<void>
}

export const ParcelaItem = memo(function ParcelaItem({
  parcela,
  formaPagamento,
  contratoAtivo,
  onMedir,
  onDesfazerLiberar,
  onPagar,
  onDesfazerPagar,
  onEditar,
  onRemover,
}: Props) {
  const [confirmRemover, setConfirmRemover] = useState(false)
  const hoje = new Date().toISOString().slice(0, 10)
  const vencida =
    parcela.status !== 'paga' && Boolean(parcela.dataPrevista && parcela.dataPrevista < hoje)
  const id = parcela.id!

  return (
    <li
      className={`cf3-parcela cf3-parcela--${parcela.status}${vencida ? ' cf3-parcela--vencida' : ''}`}
    >
      <span className='cf3-parcela__marker' aria-hidden>
        {parcela.status === 'paga' ? '✓' : parcela.ordem}
      </span>

      <div className='cf3-parcela__body'>
        <div className='cf3-parcela__top'>
          <span className='cf3-parcela__desc'>{parcela.descricao}</span>
          <span className='cf3-parcela__valor'>{formatCurrency(parcela.valor)}</span>
        </div>

        <div className='cf3-parcela__meta'>
          <Badge
            label={PARCELA_STATUS_LABEL[parcela.status]}
            variant={vencida ? 'danger' : PARCELA_STATUS_VARIANT[parcela.status]}
            size='sm'
          />
          <span className='cf3-parcela__cond'>{CONDICAO_LABEL[parcela.condicao]}</span>
          {parcela.status === 'paga' && parcela.dataPagamento ? (
            <span className='cf3-parcela__data'>
              Pago em {formatDate(parcela.dataPagamento)}
              {formaPagamento ? ` · ${FORMA_LABEL[formaPagamento] ?? formaPagamento}` : ''}
            </span>
          ) : parcela.dataPrevista ? (
            <span className='cf3-parcela__data'>
              {vencida ? 'Venceu em' : 'Previsto'} {formatDate(parcela.dataPrevista)}
            </span>
          ) : null}
        </div>

        {parcela.observacoes && <p className='cf3-parcela__obs'>{parcela.observacoes}</p>}

        {parcela.medicao && <MedicaoResumo medicao={parcela.medicao} />}

        {contratoAtivo && (
          <div className='cf3-parcela__acoes'>
            {parcela.status === 'prevista' && (
              <>
                <button
                  type='button'
                  className='cf3-parcela__btn cf3-parcela__btn--primary'
                  onClick={() => onMedir(parcela)}
                >
                  Serviço medido, liberar
                </button>
                <button type='button' className='cf3-parcela__btn' onClick={() => onPagar(parcela)}>
                  Pagar
                </button>
              </>
            )}
            {parcela.status === 'liberada' && (
              <>
                <button
                  type='button'
                  className='cf3-parcela__btn cf3-parcela__btn--primary'
                  onClick={() => onPagar(parcela)}
                >
                  Registrar pagamento
                </button>
                <button
                  type='button'
                  className='cf3-parcela__btn'
                  onClick={() => onDesfazerLiberar(id)}
                >
                  Desfazer liberação
                </button>
              </>
            )}
            {parcela.status === 'paga' && (
              <button
                type='button'
                className='cf3-parcela__btn'
                onClick={() => onDesfazerPagar(id)}
              >
                Desfazer pagamento
              </button>
            )}
            {parcela.status !== 'paga' && (
              <>
                <button
                  type='button'
                  className='cf3-parcela__btn'
                  onClick={() => onEditar(parcela)}
                >
                  Editar
                </button>
                {!confirmRemover ? (
                  <button
                    type='button'
                    className='cf3-parcela__btn cf3-parcela__btn--danger'
                    onClick={() => setConfirmRemover(true)}
                  >
                    Remover
                  </button>
                ) : (
                  <>
                    <button
                      type='button'
                      className='cf3-parcela__btn cf3-parcela__btn--danger'
                      onClick={() => onRemover(id)}
                    >
                      Confirmar remoção
                    </button>
                    <button
                      type='button'
                      className='cf3-parcela__btn'
                      onClick={() => setConfirmRemover(false)}
                    >
                      Cancelar
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </li>
  )
})

function MedicaoResumo({ medicao }: { medicao: ParcelaMedicao }) {
  const ok = medicao.itens.filter((i) => i.ok).length
  const fotos = medicao.fotoIds?.length ?? 0
  const partes = [`${ok}/${medicao.itens.length} conferidos`]
  if (fotos > 0) partes.push(`${fotos} foto${fotos > 1 ? 's' : ''}`)

  return (
    <details className='cf3-parcela__medicao'>
      <summary>Vistoria · {partes.join(' · ')}</summary>
      <div className='cf3-parcela__medicao-corpo'>
        {medicao.itens.length > 0 && (
          <ul className='cf3-parcela__medicao-lista'>
            {medicao.itens.map((item, idx) => (
              <li key={`${idx}-${item.texto}`} className={item.ok ? 'is-ok' : undefined}>
                {item.texto}
              </li>
            ))}
          </ul>
        )}
        {medicao.observacoes && <p>{medicao.observacoes}</p>}
        {medicao.diarioId != null && (
          <Link className='cf3-parcela__medicao-link' to={ROUTES.DIARIO}>
            Ver no diário
          </Link>
        )}
      </div>
    </details>
  )
}
